import React, { useCallback, useState } from "react";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  Text,
  Link,
  Box,
} from "@shopify/polaris";
import { useMutation } from "@apollo/client";
import Cookies from "js-cookie";
import {
  LOGIN_MUTATION,
  LINK_SHOP_TO_COMPANY,
} from "../../graphql/mutations";
import ShareStockLogo from "../../Components/Pictures/ShareStockLogo";
import { setMarketplaceToken } from "../../utils/marketplaceToken";

const SIGNUP_URL = "https://mysharestock.org/signup";

type ConnectionStatus = {
  shopDomain: string;
  linked: boolean;
  linkedToCurrentCompany: boolean;
};

type LinkStoreProps = {
  status: ConnectionStatus | null;
  statusErrorMessage: string | null;
  refetchStatus: () => Promise<void>;
};

const LinkStore: React.FC<LinkStoreProps> = ({
  status,
  statusErrorMessage,
  refetchStatus,
}) => {
  const shopDomain = status?.shopDomain ?? null;

  if (statusErrorMessage) {
    return (
      <Page narrowWidth title="Link your store">
        <Layout>
          <Layout.Section>
            <Banner title="Couldn't check store connection" tone="critical">
              <Text as="p" variant="bodyMd">
                {statusErrorMessage}
              </Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <SignedOutView shopDomain={shopDomain} onLinked={refetchStatus} />
  );
};

// -- Signed-out: Sign-in only. Account creation is intentionally routed to
// the marketplace signup page (mysharestock.org/signup) — collecting company
// details, agreeing to terms, and verifying an email is a heavier flow than
// belongs inside the embedded admin, and doing it on the marketplace also
// lets the merchant confirm their email before coming back to link.

const SignedOutView: React.FC<{
  shopDomain: string | null;
  onLinked: () => Promise<void>;
}> = ({ shopDomain, onLinked }) => {
  return (
    <Page narrowWidth>
      <BlockStack gap="500">
        <BlockStack gap="200" inlineAlign="center">
          <ShareStockLogo size={80} />
          <Text as="h1" variant="headingLg" alignment="center">
            Connect ShareStock to your store
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
            {shopDomain
              ? `Sign in to your ShareStock account to link ${shopDomain}.`
              : "Sign in to your ShareStock account to continue."}
          </Text>
        </BlockStack>

        <Card>
          <Box padding="300">
            <SignInForm shopDomain={shopDomain} onLinked={onLinked} />
          </Box>
        </Card>

        <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
          Don't have a ShareStock account?{" "}
          <Link url={SIGNUP_URL} target="_blank">
            Create one on mysharestock.org
          </Link>
          .
        </Text>
      </BlockStack>
    </Page>
  );
};

const SignInForm: React.FC<{
  shopDomain: string | null;
  onLinked: () => Promise<void>;
}> = ({ shopDomain, onLinked }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [login, { loading: loginLoading }] = useMutation(LOGIN_MUTATION);
  const [link, { loading: linkLoading }] = useMutation(LINK_SHOP_TO_COMPANY);
  const loading = loginLoading || linkLoading;

  // Combined login + link in one handler. The marketplace JWT only needs to
  // survive between two awaited mutations in the same synchronous JS context
  // — no iframe reload can interrupt that. Persists the cookie for normal
  // browsers; the in-memory write covers incognito where 3P cookies are
  // blocked. After the shop is linked, future requests authenticate via the
  // shop-derived companyId in middleware/auth.js, so the token doesn't need
  // to persist past this one mutation pair.
  const handleSubmit = useCallback(async () => {
    setErrorMessage(null);
    if (!shopDomain) {
      setErrorMessage("Couldn't determine your store. Reload the app from Shopify admin.");
      return;
    }
    try {
      const { data: loginData } = await login({ variables: { email, password } });
      const token = loginData?.login?.token;
      if (!token) {
        setErrorMessage("Sign-in failed. Please try again.");
        return;
      }
      Cookies.set("authToken", token, {
        secure: true,
        sameSite: "None",
        expires: 1,
      });
      setMarketplaceToken(token);

      const { data: linkData } = await link({ variables: { shopDomain } });
      if (linkData?.linkShopToCompany?.success) {
        await onLinked();
        return;
      }
      setErrorMessage(
        linkData?.linkShopToCompany?.message || "Could not link this store."
      );
    } catch (error: any) {
      setErrorMessage(extractErrorMessage(error));
    }
  }, [email, password, shopDomain, login, link, onLinked]);

  return (
    <BlockStack gap="400">
      {errorMessage && (
        <Banner tone="critical" onDismiss={() => setErrorMessage(null)}>
          {errorMessage}
        </Banner>
      )}
      <FormLayout>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoFocus
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
      </FormLayout>
      <Button
        variant="primary"
        size="large"
        fullWidth
        loading={loading}
        disabled={!email || !password}
        onClick={handleSubmit}
      >
        Sign in
      </Button>
    </BlockStack>
  );
};

// -- helpers ------------------------------------------------------------------

function extractErrorMessage(error: any): string {
  if (error?.graphQLErrors?.length) return error.graphQLErrors[0].message;
  if (error?.networkError) return "Network error: please check your connection.";
  return error?.message || "An unexpected error occurred.";
}

export default LinkStore;
